// src/pages/Donate.tsx
import React, { useState } from 'react';
import '../styles/Donate.css';

interface DonationAmount {
  value: number;
  label: string;
}

const Donate: React.FC = () => {
  const [amount, setAmount] = useState<number | ''>('');
  const [customAmount, setCustomAmount] = useState<boolean>(false);
  const [donorName, setDonorName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [donationType, setDonationType] = useState<string>('one-time');
  const [paymentMethod, setPaymentMethod] = useState<string>('card');
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);

  const predefinedAmounts: DonationAmount[] = [
    { value: 25, label: '$25' },
    { value: 50, label: '$50' },
    { value: 100, label: '$100' },
    { value: 250, label: '$250' },
    { value: 500, label: '$500' },
    { value: 1000, label: '$1,000' },
  ];

  const handleAmountSelect = (value: number) => {
    setAmount(value);
    setCustomAmount(false);
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? '' : Number(e.target.value);
    setAmount(value);
  };

  const enableCustomAmount = () => {
    setAmount('');
    setCustomAmount(true);
    setTimeout(() => {
      document.getElementById('custom-amount-input')?.focus();
    }, 50);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Proceed to next step or submit form
    if (step === 1) {
      if (amount !== '' && Number(amount) > 0) {
        setStep(2);
        window.scrollTo(0, 0);
      }
    } else {
      // In a real app, you would process the donation here
      alert('Thank you for your donation of $' + amount);
      // Reset form or redirect
    }
  };

  const goBack = () => {
    setStep(1);
    window.scrollTo(0, 0);
  };

  return (
    <div className="donate-page">
      <div className="donate-hero">
        <div className="donate-hero-content">
          <h1>Support Our Mission</h1>
          <p>Your contribution helps us create a sustainable and just future for all.</p>
        </div>
      </div>
      
      <div className="donate-container">
        <div className="donate-sidebar">
          <div className="donate-impact">
            <h3>Your Impact</h3>
            <div className="impact-item">
              <div className="impact-icon">🌱</div>
              <div className="impact-text">
                <h4>$25</h4>
                <p>Plants 10 trees in urban communities</p>
              </div>
            </div>
            <div className="impact-item">
              <div className="impact-icon">🏫</div>
              <div className="impact-text">
                <h4>$50</h4>
                <p>Provides educational materials for 5 students</p>
              </div>
            </div>
            <div className="impact-item">
              <div className="impact-icon">🌞</div>
              <div className="impact-text">
                <h4>$100</h4>
                <p>Supports a clean energy workshop for 1 community</p>
              </div>
            </div>
            <div className="impact-item">
              <div className="impact-icon">🌊</div>
              <div className="impact-text">
                <h4>$500</h4>
                <p>Funds water conservation projects for a month</p>
              </div>
            </div>
          </div>
          
          <div className="donate-testimonial">
            <blockquote>
              "Supporting EmpowerFuture21 has been one of the most fulfilling decisions I've made. I've seen firsthand how they transform communities."
            </blockquote>
            <cite>— Maria Johnson, Monthly Donor</cite>
          </div>
        </div>
        
        <div className="donate-form-container">
          <div className="donate-steps">
            <div className={`step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-text">Amount</div>
            </div>
            <div className="step-connector"></div>
            <div className={`step ${step === 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-text">Payment</div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="donate-form">
            {step === 1 && (
              <div className="donation-amount-section">
                <h2>Choose Your Donation Amount</h2>
                
                <div className="donation-type-selector">
                  <button 
                    type="button"
                    className={donationType === 'one-time' ? 'active' : ''}
                    onClick={() => setDonationType('one-time')}
                  >
                    One-Time
                  </button>
                  <button 
                    type="button"
                    className={donationType === 'monthly' ? 'active' : ''}
                    onClick={() => setDonationType('monthly')}
                  >
                    Monthly
                  </button>
                </div>
                
                <div className="amount-options">
                  {predefinedAmounts.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={amount === option.value ? 'active' : ''}
                      onClick={() => handleAmountSelect(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={customAmount ? 'active' : ''}
                    onClick={enableCustomAmount}
                  >
                    Custom
                  </button>
                </div>
                
                {customAmount && (
                  <div className="custom-amount">
                    <label htmlFor="custom-amount-input">Enter Amount</label>
                    <div className="custom-amount-input-container">
                      <span className="currency-symbol">$</span>
                      <input
                        id="custom-amount-input"
                        type="number"
                        min="1"
                        step="1"
                        value={amount === '' ? '' : amount}
                        onChange={handleCustomAmountChange}
                        placeholder="Other amount"
                      />
                    </div>
                  </div>
                )}
                
                <button type="submit" className="donate-button" disabled={amount === '' || Number(amount) <= 0}>
                  Continue to Payment
                </button>
              </div>
            )}
            
            {step === 2 && (
              <div className="donation-payment-section">
                <h2>Your Donation Details</h2>
                
                <div className="donation-summary">
                  <p>
                    {donationType === 'one-time' ? 'One-time donation' : 'Monthly donation'} of{' '}
                    <strong>${typeof amount === 'number' ? amount.toFixed(2) : amount}</strong>
                  </p>
                </div>
                
                <div className="donor-info">
                  <div className="form-group">
                    <label htmlFor="donor-name">Your Name</label>
                    <input
                      id="donor-name"
                      type="text"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      required={!isAnonymous}
                      disabled={isAnonymous}
                      placeholder="John Smith"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="donor-email">Email Address</label>
                    <input
                      id="donor-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div className="form-group checkbox">
                    <input
                      id="anonymous"
                      type="checkbox"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                    />
                    <label htmlFor="anonymous">Make my donation anonymous</label>
                  </div>
                </div>
                
                <div className="payment-method">
                  <h3>Payment Method</h3>
                  
                  <div className="payment-options">
                    <button
                      type="button"
                      className={paymentMethod === 'card' ? 'active' : ''}
                      onClick={() => setPaymentMethod('card')}
                    >
                      Credit Card
                    </button>
                    <button
                      type="button"
                      className={paymentMethod === 'paypal' ? 'active' : ''}
                      onClick={() => setPaymentMethod('paypal')}
                    >
                      PayPal
                    </button>
                  </div>
                  
                  {paymentMethod === 'card' && (
                    <div className="card-payment-form">
                      <div className="form-group">
                        <label htmlFor="card-number">Card Number</label>
                        <input
                          id="card-number"
                          type="text"
                          placeholder="**** **** **** ****"
                          required
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="expiry">Expiry Date</label>
                          <input
                            id="expiry"
                            type="text"
                            placeholder="MM/YY"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="cvv">CVV</label>
                          <input
                            id="cvv"
                            type="text"
                            placeholder="123"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {paymentMethod === 'paypal' && (
                    <div className="paypal-info">
                      <p>You will be redirected to PayPal to complete your donation.</p>
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button type="button" className="back-button" onClick={goBack}>
                    Back
                  </button>
                  <button type="submit" className="donate-button">
                    Complete Donation
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Donate;